import Vue from 'vue'

const POLL_INTERVAL_MS = 15000

const state = {
    is_blocked: false,
    is_warning_only: false,
    bypass_governance: false,
    rooms: [],
    total_critical_slots: 0,
    applied_at: null,
    threshold: 3,
    suggested_context: null,
    loading: false,
    last_fetched_at: null,
    poll_handle: null,
}

const getters = {
    isBlocked: (s) => Boolean(s.is_blocked) && !s.bypass_governance,
    isWarningOnly: (s) => Boolean(s.is_warning_only) || (s.bypass_governance && s.rooms.length > 0),
    blockingRoomIds: (s) => s.rooms.map((r) => r.id),
    blockingRooms: (s) => s.rooms,
    threshold: (s) => s.threshold,
    bypassGovernance: (s) => Boolean(s.bypass_governance),
    suggestedContext: (s) => s.suggested_context,
    totalCriticalSlots: (s) => Number(s.total_critical_slots) || 0,
}

const mutations = {
    SET_LOADING(s, loading) { s.loading = Boolean(loading) },
    APPLY_STATE(s, payload) {
        s.is_blocked = Boolean(payload?.is_blocked)
        s.is_warning_only = Boolean(payload?.is_warning_only)
        s.bypass_governance = Boolean(payload?.bypass_governance)
        s.rooms = Array.isArray(payload?.rooms) ? payload.rooms : []
        s.total_critical_slots = Number(payload?.total_critical_slots) || 0
        s.applied_at = payload?.applied_at || null
        s.threshold = Number(payload?.threshold || 3)
        s.suggested_context = payload?.suggested_context || null
        s.last_fetched_at = new Date().toISOString()
    },
    SET_POLL_HANDLE(s, handle) { s.poll_handle = handle },
    CLEAR(s) {
        s.is_blocked = false
        s.is_warning_only = false
        s.bypass_governance = false
        s.rooms = []
        s.applied_at = null
        if (s.poll_handle) {
            clearInterval(s.poll_handle)
            s.poll_handle = null
        }
    },
}

const actions = {
    async ActionFetchBlockState({ commit, rootGetters }) {
        // EP-958: não disparar fetch sem auth — 401 cai no interceptor
        // global e força logout, derrubando a sessão recém-aberta.
        if (!rootGetters['auth/hasToken']) {
            return null
        }
        commit('SET_LOADING', true)
        try {
            const response = await Vue.http.get('governance/block-state')
            const body = response?.body ?? response?.data ?? {}
            commit('APPLY_STATE', body)
            return body
        } catch (error) {
            // 423 Locked também tem corpo válido — alguns clientes throwam.
            const status = error?.status || error?.response?.status
            const body = error?.body || error?.response?.data
            if (status === 423 && body) {
                commit('APPLY_STATE', body)
                return body
            }
            return null
        } finally {
            commit('SET_LOADING', false)
        }
    },

    ActionStartPolling({ state: s, dispatch, commit }) {
        if (s.poll_handle) return
        const handle = setInterval(() => {
            if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return
            dispatch('ActionFetchBlockState')
        }, POLL_INTERVAL_MS)
        commit('SET_POLL_HANDLE', handle)
    },

    ActionStopPolling({ state: s, commit }) {
        if (s.poll_handle) {
            clearInterval(s.poll_handle)
            commit('SET_POLL_HANDLE', null)
        }
    },

    ActionApplyLockedFromInterceptor({ commit }, payload) {
        commit('APPLY_STATE', {
            is_blocked: true,
            rooms: (payload?.room_ids || []).map((id) => ({ id })),
            bypass_governance: false,
            is_warning_only: false,
        })
    },

    ActionClear({ commit }) {
        commit('CLEAR')
    },

    /**
     * Auto-seleciona o contexto (instituição + período + campi) sugerido
     * pelo backend para que o usuário caia direto na tela onde resolve
     * o bloqueio. Usado pelo botão "Ver detalhes" do overlay/banner.
     */
    async ActionSelectSuggestedContext({ state: s }) {
        const ctx = s.suggested_context
        if (!ctx || !ctx.institution_id || !ctx.work_time_id) {
            return false
        }
        const campuses = (ctx.campus_ids || []).map((id) => ({ id }))
        if (!campuses.length) return false
        try {
            await Vue.http.post('auth/selected', {
                institution_id: ctx.institution_id,
                work_time_id: ctx.work_time_id,
                campuses,
            })
            return true
        } catch {
            return false
        }
    },
}

export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions,
}
